import { Component, type ReactNode, type ErrorInfo } from 'react'
import { captureError } from '@/lib/errors'
import { Button } from '@/components/ui/shadcn/button'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    captureError(error, {
      flow: 'react-render',
      componentStack: info.componentStack,
    })
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultFallback />
    }
    return this.props.children
  }
}

function DefaultFallback() {
  return (
    <div className="bg-background flex h-screen items-center justify-center p-6">
      <div className="max-w-md text-center">
        <h1 className="text-foreground text-xl font-semibold">Something went wrong</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          The error has been reported. Try refreshing the page.
        </p>
        <Button onClick={() => window.location.reload()} className="mt-4">
          Reload
        </Button>
      </div>
    </div>
  )
}
